import {
  ComputeElementStatsRequest,
  ComputeElementStatsResponse,
  ComputeMetricResponse,
  MultiDatum,
} from '@fullstory/proto/fs/services/alexa';
import { int64 } from '@fullstory/proto/google/protobuf';
import { isEmpty } from 'lodash';
import { EventSearch, Scope } from '@fullstory/proto/fs/api/search';
import { Behavior, Constraints, SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { DirectFocus } from './actions/heatmapRequestActions';
import { assertExhaustive } from '@fullstory/typeutils';

export const modifyMetricToElementStats = (
  result: ComputeMetricResponse,
  statsType: ComputeElementStatsRequest.StatsType,
  focusedOnlyOn?: DirectFocus,
): ComputeElementStatsResponse => {
  const defaultMapping = {
    numMatchingIndividuals: int64.fromNumber(0),
    canonicalPageUrl: '',
    pageDefId: 0,
    pageViewEventStart: null,
    statsType,
    countResult: null,
  };

  if (!result.data || result.data.table.length === 0) return defaultMapping;
  const resultData = result.data;

  const selectorCounts: Array<MultiDatum> = [];
  const elementCounts: Array<MultiDatum> = [];
  const elementPropertyCounts: Array<ComputeElementStatsResponse.MultiDatumElementProperty> = [];

  resultData.table
    .filter(row => row.defaultValue > 0)
    .forEach(t => {
      const groups = [...t.groups];
      const emptyGroupForDimResults = isEmpty(t.groups[0]?.groupId) && resultData.table.length > 1;
      if (focusedOnlyOn && !emptyGroupForDimResults) {
        switch (focusedOnlyOn.type) {
          case 'selector':
            groups.unshift({ groupId: focusedOnlyOn.selector, fallback: true });
            break;
          case 'element':
            groups.unshift({ groupId: focusedOnlyOn.id, fallback: false });
            break;
          default:
            assertExhaustive(focusedOnlyOn);
        }
      }
      if (isEmpty(groups[0]?.groupId)) return;
      const datum = {
        label: groups[0]!.groupId,
        values: [t.defaultValue, t.value],
      };
      const propertyValue = groups[1]?.groupId;
      if (propertyValue !== undefined) {
        const labelKind = groups[0]?.fallback
          ? ComputeElementStatsResponse.MultiDatumElementProperty.LabelKind.LABEL_KIND_SELECTOR
          : ComputeElementStatsResponse.MultiDatumElementProperty.LabelKind.LABEL_KIND_ELEMENT;
        const datumElementProperty = {
          labelKind,
          datum,
          propertyValue,
        };
        elementPropertyCounts.push(datumElementProperty);
      } else if (groups[0]?.fallback) {
        selectorCounts.push(datum);
      } else {
        elementCounts.push(datum);
      }
    });

  return {
    ...defaultMapping,
    countResult: {
      total: int64.fromNumber(
        result.data.table.find(t => t.groups.length === 0)?.defaultValue ?? 0,
      ),
      counts: selectorCounts,
      uniqueUsers: int64.fromNumber(result.data.table.find(t => t.groups.length === 0)?.value ?? 0),
      namedElementCounts: elementCounts,
      elementPropertyCounts,
    },
    numMatchingIndividuals: int64.fromNumber(
      result.data.table.find(t => t.groups.length === 0)?.value ?? 0,
    ),
  };
};

export const generateScrollMapEventSearch = ({
  behavior,
  constraints,
  scopedSegmentDefinition,
  addSelectForPageDef,
}: {
  behavior: Behavior;
  constraints: Constraints;
  scopedSegmentDefinition: SegmentDefinition;
  // If true, add a select cause that will cause us to join on PageView docs. Mainly used to enhance queries that use page vars
  addSelectForPageDef: boolean;
}): EventSearch => {
  const select: EventSearch.Select[] = [];

  if (addSelectForPageDef) {
    const pageDefIdFilter = behavior.event?.deps.find(dep => dep.pageDefId);

    if (pageDefIdFilter) {
      select.push({
        events: [],
        behaviors: [
          {
            event: {
              primary: {
                visitedPage: pageDefIdFilter.pageDefId,
              },
              deps: [],
            },
          },
        ],
        platform: constraints.platform,
        timeRange: constraints.timeRange,
        completeSessions: constraints.completeSessions,
      });
    }
  }

  return {
    for: [scopedSegmentDefinition],
    matchingBehaviors: [
      {
        behaviors: [behavior],
        excludeBehaviors: [],
        constraints,
        excludeConstraints: null,
        // segment definition items should be at the session level and other items at the PageView level
        widenToScope: Scope.SCOPE_PAGE_VIEW,
      },
      {
        behaviors: scopedSegmentDefinition.behaviors,
        excludeBehaviors: scopedSegmentDefinition.excludeBehaviors,
        constraints: scopedSegmentDefinition.constraints,
        excludeConstraints: scopedSegmentDefinition.excludeConstraints,
        // segment definition items should be at the session level and other items at the PageView level
        widenToScope: Scope.SCOPE_SESSION,
      },
    ],
    select,
  };
};
